// Offline əməliyyat NÖVBƏSİ — offline yaradılan sifarişlər (və sonra ödənişlər) burada saxlanır,
// online qayıdanda avtomatik /api/sync/ops-a göndərilir. Hər op idempotent op_id daşıyır →
// server təkrar tətbiq etmir (ikiqat sifariş olmaz). YALNIZ "Offline rejim" tikli tenantda işləyir.
// Additive: online davranışa toxunmur; flag sönülüdürsə növbəyə heç nə yazılmır.

import api from '../api/axios';
import { offlineEnabled, graceValidToday } from './offlineCache';
import { isOnline, isOffline } from './netStatus';

const QUEUE_KEY = 'pos_offline_queue';
// Son real JWT (sinxron üçün). C# açılışda sessiyanı silir, 'pos_offline_queue' prefiksi isə saxlanır →
// proqram offline yenidən açılıb PIN ilə girilsə belə, internet qayıdanda satışlar girişi gözləmədən göndərilir.
const SYNC_TOKEN_KEY = 'pos_offline_queue_jwt';
// «Ləğv» ilə növbədən çıxarılan (serverin qəbul etmədiyi) op-lar → arxiv (iz itməsin)
const FAILED_KEY = 'pos_offline_queue_failed';

export const rememberSyncToken = () => {
    try {
        const t = localStorage.getItem('pos_token');
        if (t && t !== 'offline') localStorage.setItem(SYNC_TOKEN_KEY, t);
    } catch { /* ignore */ }
};
const syncToken = () => {
    try {
        const t = localStorage.getItem('pos_token');
        if (t && t !== 'offline') return t;
        return localStorage.getItem(SYNC_TOKEN_KEY) || '';
    } catch { return ''; }
};

// Offline əməliyyat qəbulu aktivdir? (flag açıq + şəbəkə yox + bugünkü güzəşt etibarlı)
export const isOfflineActive = () => offlineEnabled() && graceValidToday() && isOffline();

const uuid = () => {
    try { if (window.crypto?.randomUUID) return window.crypto.randomUUID(); } catch { /* ignore */ }
    return 'op-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
};

export const getQueue = () => {
    try { return JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]'); } catch { return []; }
};

const saveQueue = (q) => {
    try { localStorage.setItem(QUEUE_KEY, JSON.stringify(q)); } catch { /* dolubsa səssiz */ }
    emit();
};

export const queueCount = () => getQueue().length;

// Server tərəfindən RƏDD edilmiş op sayı (son flush-da xəta alan, _err işarəli)
export const failedCount = () => getQueue().filter((o) => o._err).length;

// YALNIZ ilişən (serverin rədd etdiyi) op-ları növbədən çıxar → arxivə yaz.
// Uğurlu ola biləcək satışlar, draft-lar və offline əlavələr TOXUNULMAZ qalır.
export const discardFailedOps = () => {
    const q = getQueue();
    const bad = q.filter((o) => o._err);
    if (!bad.length) return 0;
    try {
        const arch = JSON.parse(localStorage.getItem(FAILED_KEY) || '[]');
        const now = new Date().toISOString();
        bad.forEach((o) => arch.push({ ...o, discarded_at: now }));
        localStorage.setItem(FAILED_KEY, JSON.stringify(arch.slice(-200)));
    } catch { /* ignore */ }
    saveQueue(q.filter((o) => !o._err));
    return bad.length;
};

// Offline aktivliyi olan masa id-ləri (Masalar ekranında narıncı göstərici üçün):
//   - yarımçıq draft (offdraft:<id>, məhsulu var)  → hələ tutulur
//   - növbədə gözləyən AÇIQ offline sifariş (order.createComplete)  → sinxron gözləyir
// (order.createAndPay bağlanmış sayılır → masa boşdur, göstərici yox)
// Bir masanın offline draft-ını qaytar (waiter_id, items, note) — Masalar ekranı picker-i atlamaq üçün
export const getOfflineDraft = (tableId) => {
    try { return JSON.parse(localStorage.getItem('offdraft:' + tableId) || 'null'); } catch { return null; }
};

export const getOfflineTableIds = () => {
    const ids = new Set();
    try {
        for (let i = 0; i < localStorage.length; i++) {
            const k = localStorage.key(i);
            if (k && k.startsWith('offdraft:')) {
                const d = JSON.parse(localStorage.getItem(k) || 'null');
                if (d && Array.isArray(d.items) && d.items.length) ids.add(k.slice('offdraft:'.length));
            }
        }
    } catch { /* ignore */ }
    try {
        for (const op of getQueue()) {
            if (op.type === 'order.createComplete' && op.payload?.table_id != null) {
                ids.add(String(op.payload.table_id));
            }
        }
    } catch { /* ignore */ }
    return ids;
};

// Online açılıb OFFLINE bağlanmış sifarişlər (növbədə order.payExisting) → masa boş göstərilsin
export const getOfflinePaidOrderIds = () => {
    const ids = new Set();
    try {
        for (const op of getQueue()) {
            if (op.type === 'order.payExisting' && op.payload?.order_id != null) ids.add(String(op.payload.order_id));
        }
    } catch { /* ignore */ }
    return ids;
};

// Sadə abunə mexanizmi — UI (banner) növbə sayını izləsin
const listeners = new Set();
export const onQueueChange = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
const emit = () => { listeners.forEach((fn) => { try { fn(queueCount()); } catch { /* ignore */ } }); };

// Yeni op növbəyə: {op_id, type, payload, client_ts}. Stabil op_id qaytarır (idempotentlik).
export const enqueueOp = (type, payload) => {
    const op = { op_id: uuid(), type, payload: payload || {}, client_ts: new Date().toISOString() };
    const q = getQueue();
    q.push(op);
    saveQueue(q);
    return op.op_id;
};

// Növbədəki bir op-u op_id ilə yenilə (məs. offline sifarişə yeni item əlavə ediləndə)
export const updateQueuedPayload = (opId, payload) => {
    const q = getQueue();
    const i = q.findIndex((o) => o.op_id === opId);
    if (i === -1) return false;
    q[i].payload = payload;
    q[i].client_ts = q[i].client_ts || new Date().toISOString();
    saveQueue(q);
    return true;
};

// Masanın növbədəki AÇIQ offline sifarişini (order.createComplete) növbədən çıxarıb draft kimi qaytar.
// Yalnız offline-da çağırılır (flush işləmir → op hələ serverə getməyib).
export const takeQueuedDraft = (tableId) => {
    if (!isOffline()) return null;
    const q = getQueue();
    const ops = q.filter((o) => o.type === 'order.createComplete' && String(o.payload?.table_id) === String(tableId) && o.payload?._draft);
    if (!ops.length) return null;
    const items = [];
    let base = null;
    for (const o of ops) {
        const d = o.payload._draft || {};
        base = base || d;
        (d.items || []).forEach((it) => items.push(it));
    }
    saveQueue(q.filter((o) => !ops.includes(o)));
    return {
        items, note: base?.note || '', waiter_id: base?.waiter_id || null,
        ...(ops.some((o) => o.payload?.bill_printed || o.payload?._draft?.bill_printed) ? { bill_printed: 1 } : {}),
    };
};

export const removeOp = (opId) => {
    const q = getQueue().filter((o) => o.op_id !== opId);
    saveQueue(q);
};

// Debug: brauzer konsolundan növbəni görmək/təmizləmək (zərərsiz — yalnız localStorage)
if (typeof window !== 'undefined') {
    window.__offlineQueue = () => getQueue();
    window.__offlineFailed = () => { try { return JSON.parse(localStorage.getItem(FAILED_KEY) || '[]'); } catch { return []; } };
    // Növbəni + bütün gözləyən offline draft/əlavələri təmizlə (köhnə test qalıqlarını sıfırla)
    window.__offlineQueueClear = () => {
        saveQueue([]);
        try {
            const keys = [];
            for (let i = 0; i < localStorage.length; i++) { const k = localStorage.key(i); if (k && (k.startsWith('offdraft:') || k.startsWith('offadd:'))) keys.push(k); }
            keys.forEach((k) => localStorage.removeItem(k));
        } catch { /* ignore */ }
        return 'offline növbə + draft + əlavələr təmizləndi';
    };
}

// Stored offline sətri → sync op item formatı
const mapLine = (it) => ({
    item_id: it.item_id || null,
    custom_name: it.custom_name || null,
    custom_printer: it.custom_printer || null,
    variant_id: it._variantId || it.variant_id || null,
    qty: Number(it.qty),
    unit_price: Number(it.unit_price),
    note: it.note || null,
});

// İnternet qayıdanda GÖZLƏYƏN bütün offline dəyişiklikləri (əl ilə göndərilməmiş draft + əlavələr)
// avtomatik sync növbəsinə yaz → tam sinxron. Hər biri idempotent op_id daşıyır (dublikat yox).
//   offdraft:<tableId> → order.createComplete (yeni açıq sifariş)
//   offadd:<orderId>   → order.addItems       (mövcud sifarişə əlavə)
export const autoQueuePendingOffline = () => {
    if (!offlineEnabled()) return 0;
    let queued = 0;
    const keys = [];
    try { for (let i = 0; i < localStorage.length; i++) { const k = localStorage.key(i); if (k) keys.push(k); } }
    catch { return 0; }
    for (const k of keys) {
        try {
            if (k.startsWith('offdraft:')) {
                const tid = k.slice('offdraft:'.length);
                const d = JSON.parse(localStorage.getItem(k) || 'null');
                if (d && Array.isArray(d.items) && d.items.length) {
                    enqueueOp('order.createComplete', {
                        table_id: Number(tid), waiter_id: d.waiter_id || null, note: d.note || null,
                        already_printed: false, bill_printed: d.bill_printed ? 1 : 0, items: d.items.map(mapLine),
                    });
                    localStorage.removeItem(k); queued += 1;
                }
            } else if (k.startsWith('offadd:')) {
                const oid = k.slice('offadd:'.length);
                const arr = JSON.parse(localStorage.getItem(k) || '[]');
                if (Array.isArray(arr) && arr.length) {
                    enqueueOp('order.addItems', {
                        order_id: Number(oid), already_printed: false, items: arr.map(mapLine),
                    });
                    localStorage.removeItem(k); queued += 1;
                }
            }
        } catch { /* bu açarı keç */ }
    }
    return queued;
};

let flushing = false;

// Növbəni serverə göndər. Yalnız online olanda. Uğurlu/duplikat op-lar növbədən silinir.
// Qaytarır: { synced, failed, remaining } — banner "hər şey sinxronlaşdırıldı" üçün istifadə edir.
export const flushQueue = async () => {
    if (flushing) return { synced: 0, failed: 0, remaining: queueCount(), skipped: true };
    if (!isOnline()) return { synced: 0, failed: 0, remaining: queueCount(), offline: true };
    if (!offlineEnabled()) return { synced: 0, failed: 0, remaining: queueCount(), disabled: true };
    // Token yoxdursa / offline-placeholder-dirsə flush etmə — 401 və refresh döngüsünün qarşısı.
    // ('offline' placeholder = offline PIN login, real token yoxdur → online real login lazımdır)
    // Sessiya tokeni yoxdursa (C# restart + offline PIN) → son saxlanmış real token
    rememberSyncToken();
    const _tok = syncToken();
    if (!_tok) return { synced: 0, failed: 0, remaining: queueCount(), noAuth: true };

    const q = getQueue();
    if (!q.length) return { synced: 0, failed: 0, remaining: 0 };

    flushing = true;
    let synced = 0, failed = 0;
    try {
        // Lokal işarələr (_err, _tries) serverə getməsin
        const wire = q.map(({ _err, _tries, ...op }) => {
            if (op.payload && op.payload._draft) { const { _draft, ...pl } = op.payload; return { ...op, payload: pl }; }
            return op;
        });
        const res = await api.post('/sync/ops', { ops: wire }, { headers: { Authorization: `Bearer ${_tok}` } });
        const results = res.data?.results || {};
        const keep = [];
        const errors = [];
        for (const op of q) {
            const r = results[op.op_id];
            if (r && (r.ok || r.duplicate)) {
                synced += 1; // uğurlu və ya artıq tətbiq olunub → növbədən çıxar
            } else {
                failed += 1;
                const errMsg = (r && (r.error || r.msg)) || 'naməlum xəta';
                keep.push({ ...op, _err: errMsg, _tries: Number(op._tries || 0) + 1 }); // saxla, sonra yenə göndər
                errors.push({ type: op.type, error: errMsg });
                console.error('[offlineQueue] op uğursuz:', op.type, op.op_id, '→', errMsg);
            }
        }
        saveQueue(keep);
        return { synced, failed, remaining: keep.length, errors };
    } catch (e) {
        // Şəbəkə/server xətası → növbə toxunulmaz qalır, sonra yenə cəhd
        const msg = e?.response?.data?.msg || e?.message || 'şəbəkə xətası';
        console.error('[offlineQueue] flush xətası:', msg, e?.response?.status);
        // Saxlanmış token köhnəlib (24 saat) → at; real giriş lazımdır (növbə toxunulmaz)
        if (e?.response?.status === 401) {
            try { localStorage.removeItem(SYNC_TOKEN_KEY); } catch { /* ignore */ }
            return { synced: 0, failed: 0, remaining: q.length, noAuth: true };
        }
        return { synced: 0, failed: q.length, remaining: q.length, errors: [{ type: 'flush', error: `${e?.response?.status || ''} ${msg}`.trim() }] };
    } finally {
        flushing = false;
    }
};
